import { useState } from "react";
import { Plus } from "lucide-react";
import { Label } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { ServiceDrawer, type DrawerMode } from "@/components/services/ServiceDrawer";
import { BotoesDeProcedimento } from "./BotoesDeProcedimento";
import { NovoProcedimentoDialog } from "./NovoProcedimentoDialog";
import { SeletorDeProcedimento, type Procedimento } from "./SeletorDeProcedimento";
import type { useCatalogoDoItem } from "./use-catalogo-do-item";

/**
 * O procedimento do item: busca no catálogo, botões (favoritos, do dentista, o resto) e, por último,
 * "Novo procedimento" — o diálogo com o que a clínica ainda não tem na base. Nome novo abre o
 * cadastro completo do procedimento (o mesmo do catálogo), e o criado já entra no item.
 */
export function CampoDeProcedimento({
  nome,
  servicoId,
  modoFace,
  atribuidos,
  catalogo,
  onEscolher,
  onMudarNome,
}: {
  nome: string;
  servicoId: string | null;
  modoFace: boolean;
  atribuidos: string[];
  catalogo: ReturnType<typeof useCatalogoDoItem>;
  onEscolher: (p: Procedimento) => void;
  onMudarNome: (v: string) => void;
}) {
  const utils = trpc.useUtils();
  const [novoAberto, setNovoAberto] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode>({ kind: "closed" });
  const criado = async (id?: string) => {
    setDrawer({ kind: "closed" });
    if (!id) return;
    const p = (await utils.plano.procedimentos.fetch()).find((x) => x.id === id);
    if (p) onEscolher(p as Procedimento);
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor="item-procedimento">Procedimento</Label>
      <SeletorDeProcedimento nome={nome} servicoId={servicoId} modoFace={modoFace} onEscolher={onEscolher} onMudarNome={onMudarNome} />
      <div className="pt-1">
        <BotoesDeProcedimento
          procedimentos={catalogo.procedimentos}
          selecionadoId={servicoId}
          atribuidos={atribuidos}
          onEscolher={onEscolher}
          novo={
            <button
              type="button"
              onClick={() => setNovoAberto(true)}
              className="flex min-h-8 w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-2 py-1 text-[12.5px] font-medium text-zinc-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:text-muted-foreground"
            >
              <Plus className="h-3.5 w-3.5" /> Novo procedimento
            </button>
          }
        />
      </div>
      <NovoProcedimentoDialog
        aberto={novoAberto}
        onFechar={() => setNovoAberto(false)}
        modoFace={modoFace}
        daBase={catalogo.daBase}
        nomesDoCatalogo={catalogo.nomesDoCatalogo}
        bloqueio={catalogo.bloqueio}
        ocupado={catalogo.ocupado}
        onEscolher={catalogo.escolherDaBase}
        onCriar={
          catalogo.bloqueio
            ? undefined
            : (nomeNovo) => setDrawer({ kind: "create", inicial: { nome: nomeNovo, especialidadeCodigo: modoFace ? "HARMONIZACAO_OROFACIAL" : undefined } })
        }
      />
      <ServiceDrawer mode={drawer} onClose={() => setDrawer({ kind: "closed" })} onSaved={criado} />
    </div>
  );
}
